'use client';

import React, { Suspense, useContext } from 'react';

import { sendAnalytics } from '@storefront/app/analytics/SimpleTriggerEvent';
import { FlagsContext } from '@storefront/context/LDFlagsContext';
import useGetPartnerCompensation from '@storefront/hooks/useGetPartnerCompensation';
import usePartner from '@storefront/hooks/usePartner';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import Image from 'next/image';
import { ErrorBoundary } from 'react-error-boundary';
import Skeleton from 'react-loading-skeleton';

import Breadcrumb from './breadcrumb/breadcrumb';
import styles from './header.module.scss';
import { buildPath } from '@storefront/utils/utils';
import { NamespacesI18n } from '@storefront/i18n';
import { useTranslation } from 'react-i18next';
const defaultURL = 'https://www.hp.com/us-en/shop/vwa/printers';

type BreadcrumbWrapperProps = {
  hpChannel: string;
};

const BreadcrumbWrapper = ({ hpChannel }: BreadcrumbWrapperProps) => {
  const { data, error } = useGetPartnerCompensation(hpChannel);
  const { clientReferer } = useContext(FlagsContext);
  const onClickHandler = async () => {
    const partnerUrl =
      data?.getPartnerCompensation?.hp_off_ramp_redirect_url ?? defaultURL;
    const partnerName =
      data?.getPartnerCompensation?.hp_partner_name ?? 'HpStore';
    await sendAnalytics({
      simpleUiEvent: uiEvents.breadcrumbEvent,
      override: {
        controlName: 'Partner',
        controlDetail: `partnerUrl=${partnerUrl}&name=${partnerName}`,
      },
    });
  };

  return (
    <Breadcrumb
      partnerCompensation={data}
      error={error}
      onClick={onClickHandler}
      defaultURL={defaultURL}
      clientReferrer={clientReferer}
    />
  );
};

const DefaultBreadcrumb = (
  onClickHandler: () => void,
  clientReferer: string
) => (
  <Breadcrumb
    onClick={onClickHandler}
    defaultURL={defaultURL}
    clientReferrer={clientReferer}
  />
);

export const Header = () => {
  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG, {
    keyPrefix: 'pdpConfig.headerV2',
  });
  const { clientReferer } = useContext(FlagsContext);

  const { hpChannel } = usePartner();

  const onClickHandler = async () => {
    await sendAnalytics({
      simpleUiEvent: uiEvents.breadcrumbEvent,
      override: {
        controlName: 'HpStore',
        controlDetail: `partnerUrl=${defaultURL}&name=HpStore`,
      },
    });
  };

  return (
    <div
      className={styles['top-bar']}
      data-testid="logo-element"
      id="logo-element"
    >
      <div className={styles['container']}>
        <div className={styles['left-section']}>
          <Image
            src={buildPath('HPLogo-blue.svg')}
            className={styles['logo']}
            width="60"
            height="60"
            alt="HP All-In Plan logo"
          />
          <ErrorBoundary
            fallback={DefaultBreadcrumb(onClickHandler, clientReferer)}
          >
            <Suspense
              fallback={
                <Skeleton
                  count={1}
                  height={30}
                  width={400}
                  style={{ marginLeft: '12px', marginTop: '12px' }}
                />
              }
            >
              <BreadcrumbWrapper hpChannel={hpChannel} />
            </Suspense>
          </ErrorBoundary>
        </div>
        <div className={styles['need-help-container']}>
          <div>
            <p className="caption-small" style={{ fontWeight: 500 }}>
              {t('needHelp')}
            </p>
          </div>
          <div>
            <p className="caption-small">{t('contactUs')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
